import React from 'react';
import { motion } from 'framer-motion';
import { Globe, Languages, Users, ShieldCheck, ArrowRight } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';
import { IMAGES } from '@/assets/images';

/**
 * MulticulturalApproach Page
 * Highlights the company's competitive advantage in navigating Japanese, English, and Asian markets.
 * Emphasizes cultural intelligence and professional communication without political or exaggerated narratives.
 */
const MulticulturalApproach: React.FC = () => {
  const { t, isEn } = useLanguage();

  // Spring animation variants for a professional, "Apple-style" feel
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: 'spring', stiffness: 300, damping: 30 }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative h-[40vh] min-h-[300px] flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.OFFICE_BUILDING_7}
            alt="Osaka Office"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="max-w-3xl"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-primary mb-6">
              {t({
                jp: '多文化へのアプローチ',
                en: 'Multicultural Approach'
              })}
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
              {t({
                jp: '日本・英語圏・中国語圏の市場を繋ぐ、確かな文化理解と専門性。',
                en: 'Bridging Japan, English-speaking, and Asian markets through deep cultural intelligence and professional expertise.'
              })}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Core Capability Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-3 gap-12"
          >
            {/* Language Proficiency */}
            <motion.div variants={fadeInUp} className="space-y-4">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-6">
                <Languages size={24} />
              </div>
              <h3 className="text-2xl font-bold">
                {t({ jp: '言語の壁を超える', en: 'Breaking Language Barriers' })}
              </h3>
              <p className="text-muted-foreground">
                {t({
                  jp: 'ビジネスレベルの日本語と英語に加え、中国語圏の市場背景に対する深い理解を備えています。AI翻訳に頼らない、文脈を捉えたコミュニケーションを提供します。',
                  en: 'Beyond business-level Japanese and English, we possess a profound understanding of Chinese-speaking market contexts, ensuring nuanced communication without reliance on AI translation.'
                })}
              </p>
            </motion.div>

            {/* Market Context */}
            <motion.div variants={fadeInUp} className="space-y-4">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-6">
                <Globe size={24} />
              </div>
              <h3 className="text-2xl font-bold">
                {t({ jp: '市場背景の理解', en: 'Market Context Intelligence' })}
              </h3>
              <p className="text-muted-foreground">
                {t({
                  jp: '各地域の商習慣や消費者の価値観を理解し、ブランドの価値を適切にローカライズします。日本市場への参入と、海外への展開を双方から支援します。',
                  en: 'We understand local business customs and consumer values, enabling us to localize brand value effectively. We support both entry into the Japanese market and global expansion.'
                })}
              </p>
            </motion.div>

            {/* Sustainable Partnership */}
            <motion.div variants={fadeInUp} className="space-y-4">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-6">
                <ShieldCheck size={24} />
              </div>
              <h3 className="text-2xl font-bold">
                {t({ jp: '長期的な信頼構築', en: 'Long-term Trust' })}
              </h3>
              <p className="text-muted-foreground">
                {t({
                  jp: '一時的な利益ではなく、持続可能な事業運営を目指します。日本国内の法令遵守を基盤とし、国際的なパートナーシップを構築します。',
                  en: 'We prioritize sustainable operations over short-term gains. Building on a foundation of strict Japanese legal compliance, we foster enduring international partnerships.'
                })}
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Specific Focus: The Bridge */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center gap-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex-1 space-y-8"
            >
              <div className="inline-block px-4 py-1 rounded-full bg-primary/10 text-primary text-sm font-semibold tracking-wider">
                {t({ jp: 'クロスボーダー支援', en: 'CROSS-BORDER SUPPORT' })}
              </div>
              <h2 className="text-3xl md:text-4xl font-bold leading-tight">
                {t({
                  jp: '日本とアジア、そして世界へ。\n多文化的な背景が、ビジネスの可能性を広げます。',
                  en: 'From Japan to Asia, and to the World.\nMulticultural backgrounds expand business potential.'
                })}
              </h2>
              <p className="text-muted-foreground text-lg">
                {t({
                  jp: '株式会社M.M.Kは、単なる通訳や翻訳の提供者ではありません。私たちは、異なる文化圏の「意図」と「価値」を翻訳し、最適なビジネスの形を提案するパートナーです。',
                  en: 'M.M.K is not just a provider of interpretation or translation. We are partners who translate the "intent" and "value" across cultures to propose the most effective business strategies.'
                })}
              </p>
              
              <ul className="space-y-4">
                {[ 
                  { jp: '日本市場へのブランド進出・ローカライズ支援', en: 'Brand entry and localization support for the Japanese market' },
                  { jp: '中国・アジア市場における消費者ニーズの分析', en: 'Analysis of consumer needs in Chinese and Asian markets' },
                  { jp: '欧米企業との円滑なビジネスコミュニケーション', en: 'Seamless business communication with Western enterprises' }
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <ArrowRight className="text-primary mt-1 shrink-0" size={18} />
                    <span>{t(item)}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="flex-1 bg-card border border-border p-8 md:p-12 rounded-2xl shadow-sm"
            >
              <div className="space-y-8">
                <div className="flex items-center gap-4 border-b border-border pb-6">
                  <Users className="text-primary" size={32} />
                  <div>
                    <h4 className="font-bold text-xl">{t({ jp: '多文化チームの強み', en: 'Multicultural Synergy' })}</h4>
                    <p className="text-sm text-muted-foreground">{t({ jp: '秦 佳玲 代表メッセージ', en: 'Message from CEO Chin Karei' })}</p>
                  </div>
                </div>
                <p className="italic text-foreground/80 leading-relaxed">
                  {t({
                    jp: '「日本で創業し、グローバルな視点を持つ。私たちは、それぞれの文化が持つ良さを尊重しながら、新しい価値を創造することを使命としています。」',
                    en: '"Founded in Japan with a global perspective. Our mission is to create new value while respecting the unique strengths of each culture."'
                  })}
                </p>
                <div className="pt-4">
                  <p className="font-mono text-sm text-muted-foreground">
                    Supported Languages: JP / EN / CN
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Philosophy Callout */}
      <section className="py-24">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto space-y-8"
          >
            <h2 className="text-3xl font-bold">
              {t({ jp: '持続可能な事業運営のために', en: 'For Sustainable Operations' })}
            </h2>
            <p className="text-muted-foreground">
              {t({
                jp: '私たちは、文化の壁を障害ではなく機会として捉えます。異なる背景を持つ顧客やパートナーとの対話を重ねることで、短期的な成功に留まらない、社会に貢献できる事業を育成していきます。',
                en: 'We view cultural barriers not as obstacles, but as opportunities. By engaging in dialogue with customers and partners from diverse backgrounds, we nurture businesses that contribute to society far beyond short-term success.'
              })}
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default MulticulturalApproach;